import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import SEO from '../components/SEO';
import LandingNav from '../components/Layout/LandingNav';
import LandingFooter from '../components/Layout/LandingFooter';

function RefundPolicy() {
  const { theme } = useTheme();
  const d = theme === 'dark';

  return (
    <div className={`min-h-screen flex flex-col ${d ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <SEO
        title="Refund Policy"
        description="PaperBrainz refund policy. Learn about our cancellation terms and refund process for our AI chatbot platform."
        path="/refund-policy"
      />
      <LandingNav />

      <div className="flex-1 pt-32 sm:pt-40 pb-24 sm:pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 sm:mb-20 max-w-2xl mx-auto">
            <h1 className={`text-5xl md:text-6xl lg:text-[64px] font-semibold tracking-tight mb-5 ${d ? 'text-white' : 'text-gray-900'}`}>
              Refund Policy
            </h1>
            <p className={`text-lg md:text-xl font-light leading-relaxed ${d ? 'text-gray-500' : 'text-gray-500'}`}>
              Understand our refund and cancellation terms
            </p>
            <p className={`text-sm font-light mt-4 ${d ? 'text-gray-600' : 'text-gray-400'}`}>
              IE Aleksandr Kirakosyan — PaperBrainz Refund Policy
            </p>
            <p className={`text-sm font-light mt-1 ${d ? 'text-gray-600' : 'text-gray-400'}`}>
              Last updated: March 26, 2026
            </p>
          </div>

          {/* Content */}
          <div className="space-y-14 sm:space-y-16">
            <div className="max-w-none">

              {/* Overview */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'} mb-4`}>
                  Overview
                </h2>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  At PaperBrainz (operated by IE Aleksandr Kirakosyan), we strive to provide the best possible service to our customers. This Refund Policy outlines the terms and conditions regarding refunds and cancellations.
                </p>
                <p className="text-gray-500 text-sm">
                  Please read this policy carefully before making a purchase. By subscribing to any of our paid plans, you acknowledge and agree to this Refund Policy.
                </p>
              </section>

              {/* 14-Day Refund Policy */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'} mb-4`}>
                  14-Day Refund Policy
                </h2>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  If you are not satisfied with your PaperBrainz subscription, you may request a full refund within <span className={`${d ? 'text-white' : 'text-gray-900'}`}>14 days</span> of your initial purchase. No questions asked.
                </p>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  For consumers in the EU and UK, this policy meets or exceeds your statutory 14-day right of withdrawal for digital services under the Consumer Rights Directive (2011/83/EU). By signing up and using the chatbot before the 14-day period ends, you acknowledge that the digital service is supplied immediately at your request. You may still withdraw within 14 days and receive a refund, even if you have used the service in that time.
                </p>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  To request a refund, simply contact us at{' '}
                  <a href="mailto:support@paperbrainz.com" className={`underline underline-offset-2 decoration-1 transition-colors ${d ? 'text-white hover:text-gray-300' : 'text-gray-900 hover:text-gray-700'}`}>
                    support@paperbrainz.com
                  </a>{' '}
                  with your account email and we will process your refund promptly.
                </p>
                <div className={`rounded-[16px] p-6 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                  <h4 className={`font-semibold ${d ? 'text-white' : 'text-gray-900'} mb-3`}>Refund Eligibility:</h4>
                  <ul className={`space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                    <li className="flex items-start">
                      <span className={`w-1 h-1 rounded-full mr-3 mt-2 flex-shrink-0 ${d ? 'bg-gray-500' : 'bg-gray-400'}`} />
                      Request must be made within 14 days of initial purchase
                    </li>
                    <li className="flex items-start">
                      <span className={`w-1 h-1 rounded-full mr-3 mt-2 flex-shrink-0 ${d ? 'bg-gray-500' : 'bg-gray-400'}`} />
                      Applies to first-time subscriptions
                    </li>
                    <li className="flex items-start">
                      <span className={`w-1 h-1 rounded-full mr-3 mt-2 flex-shrink-0 ${d ? 'bg-gray-500' : 'bg-gray-400'}`} />
                      Refunds are processed within 5-10 business days
                    </li>
                  </ul>
                </div>
              </section>

              {/* Subscription Cancellations */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'} mb-4`}>
                  Subscription Cancellations
                </h2>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  You may cancel your subscription at any time through your account dashboard or by contacting our support team.
                </p>

                <div className={`rounded-[16px] p-6 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                  <h4 className={`font-semibold ${d ? 'text-white' : 'text-gray-900'} mb-3`}>When You Cancel:</h4>
                  <ul className={`space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                    <li className="flex items-start">
                      <span className={`w-1.5 h-1.5 rounded-full mr-3 mt-2 flex-shrink-0 ${d ? 'bg-white/40' : 'bg-gray-300'}`} />
                      Your access continues until the end of your current billing period
                    </li>
                    <li className="flex items-start">
                      <span className={`w-1.5 h-1.5 rounded-full mr-3 mt-2 flex-shrink-0 ${d ? 'bg-white/40' : 'bg-gray-300'}`} />
                      No further charges will be made after cancellation
                    </li>
                    <li className="flex items-start">
                      <span className={`w-1.5 h-1.5 rounded-full mr-3 mt-2 flex-shrink-0 ${d ? 'bg-white/40' : 'bg-gray-300'}`} />
                      Your chatbot remains active until the billing period ends
                    </li>
                    <li className="flex items-start">
                      <span className={`w-1.5 h-1.5 rounded-full mr-3 mt-2 flex-shrink-0 ${d ? 'bg-white/40' : 'bg-gray-300'}`} />
                      Data and usage history are retained for 30 days after expiration
                    </li>
                    <li className="flex items-start">
                      <span className={`w-1.5 h-1.5 rounded-full mr-3 mt-2 flex-shrink-0 ${d ? 'bg-white/40' : 'bg-gray-300'}`} />
                      Refunds for unused portions are available within the 14-day window
                    </li>
                  </ul>
                </div>
              </section>

              {/* Service Credits */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'} mb-4`}>
                  Service Outages & Credits
                </h2>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  In the event of significant service disruptions that affect your chatbot's availability, we may issue service credits at our discretion.
                </p>

                <div className="overflow-x-auto">
                  <table className={`w-full border-collapse overflow-hidden rounded-[16px] border ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                    <thead className={`${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${d ? 'text-white border-white/[0.06]' : 'text-gray-900 border-gray-200'} border-b`}>Downtime Duration</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${d ? 'text-white border-white/[0.06]' : 'text-gray-900 border-gray-200'} border-b`}>Credit Amount</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${d ? 'text-white border-white/[0.06]' : 'text-gray-900 border-gray-200'} border-b`}>Plan Eligibility</th>
                      </tr>
                    </thead>
                    <tbody className={`${d ? 'text-gray-400' : 'text-gray-600'}`}>
                      <tr className={`border-b ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                        <td className="px-4 py-3">4+ hours continuous</td>
                        <td className="px-4 py-3">10% monthly fee</td>
                        <td className="px-4 py-3">Pro</td>
                      </tr>
                      <tr className={`border-b ${d ? 'border-white/[0.06]' : 'border-gray-200'}`}>
                        <td className="px-4 py-3">24+ hours continuous</td>
                        <td className="px-4 py-3">25% monthly fee</td>
                        <td className="px-4 py-3">All paid plans</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3">72+ hours continuous</td>
                        <td className="px-4 py-3">50% monthly fee</td>
                        <td className="px-4 py-3">All paid plans</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="text-sm text-gray-500 mt-4">
                  Note: Scheduled maintenance, force majeure events, and issues caused by third-party dependencies are excluded from credit calculations. Service credits are applied to future billing cycles and cannot be redeemed for cash.
                </p>
              </section>

              {/* Billing Issues */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'} mb-4`}>
                  Billing Issues
                </h2>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  If you experience any billing issues, we will resolve them promptly:
                </p>
                <ul className={`list-disc pl-6 space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li>Duplicate charges will be refunded immediately</li>
                  <li>Billing errors will be corrected and refunded</li>
                  <li>Unauthorized transactions will be investigated and resolved</li>
                </ul>
                <p className="text-gray-500 text-sm mt-4">
                  For any billing concerns, contact us at{' '}
                  <a href="mailto:support@paperbrainz.com" className={`underline underline-offset-2 decoration-1 transition-colors ${d ? 'text-white hover:text-gray-300' : 'text-gray-900 hover:text-gray-700'}`}>
                    support@paperbrainz.com
                  </a>{' '}
                  with your account details.
                </p>
              </section>

              {/* Chargebacks */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'} mb-4`}>
                  Chargebacks & Disputes
                </h2>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  We strongly encourage customers to contact us directly before initiating a chargeback or payment dispute.
                </p>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  Chargebacks initiated without prior contact may result in immediate account suspension. If you file a chargeback:
                </p>
                <ul className={`list-disc pl-6 space-y-2 ${d ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li>Your account will be immediately suspended pending investigation</li>
                  <li>Your chatbot widget will be deactivated</li>
                  <li>We will provide transaction records to your payment provider</li>
                  <li>Account reactivation requires resolution of the dispute</li>
                </ul>
              </section>

              {/* Quick Summary */}
              <section>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'} mb-4`}>
                  Quick Summary
                </h2>
                <div className={`rounded-[16px] p-6 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                  <ul className="space-y-3">
                    {[
                      '14-day refund policy on initial purchase',
                      'Cancel anytime with access until period ends',
                      'Full refunds within the 14-day window',
                      'Service credits available for extended outages',
                      'Contact support before initiating chargebacks'
                    ].map((item, i) => (
                      <li key={i} className={`flex items-center ${d ? 'text-gray-300' : 'text-gray-700'}`}>
                        <span className={`w-1 h-1 rounded-full mr-3 flex-shrink-0 ${d ? 'bg-gray-500' : 'bg-gray-400'}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* Contact Information */}
              <section className={`border-t ${d ? 'border-white/[0.06]' : 'border-gray-200'} pt-8`}>
                <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'} mb-4`}>
                  Contact Information
                </h2>
                <p className={`${d ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  For billing questions or policy clarifications, please contact:
                </p>
                <div className={`rounded-[16px] p-6 ${d ? 'bg-white/[0.03]' : 'bg-gray-50'}`}>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className={`font-medium ${d ? 'text-white' : 'text-gray-900'} mb-2`}>Billing Support</h4>
                      <p className={`${d ? 'text-gray-400' : 'text-gray-600'}`}>IE Aleksandr Kirakosyan</p>
                      <p className={`${d ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                        <a href="mailto:support@paperbrainz.com" className={`underline underline-offset-2 decoration-1 transition-colors ${d ? 'text-white hover:text-gray-300' : 'text-gray-900 hover:text-gray-700'}`}>
                          support@paperbrainz.com
                        </a>
                      </p>
                      <p className="text-sm text-gray-500 mt-1">Response time: 24-48 hours</p>
                    </div>
                    <div>
                      <h4 className={`font-medium ${d ? 'text-white' : 'text-gray-900'} mb-2`}>Legal Inquiries</h4>
                      <p className={`${d ? 'text-gray-400' : 'text-gray-600'}`}>
                        <a href="mailto:support@paperbrainz.com" className={`underline underline-offset-2 decoration-1 transition-colors ${d ? 'text-white hover:text-gray-300' : 'text-gray-900 hover:text-gray-700'}`}>
                          support@paperbrainz.com
                        </a>
                      </p>
                      <p className="text-sm text-gray-500 mt-1">For disputes and legal matters</p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}

export default RefundPolicy;
